import { GroupBase } from "chakra-react-select";
import { RefObject, useCallback, useEffect, useState } from "react";
import { SelectInstance } from "react-select";
import { OptionsOrGroups } from "react-select/dist/declarations/src/types";
import { SelectOptionData } from "shared/components/input/select/SelectTypes";
import { isSelectOptionData } from "shared/components/input/select/SelectUtils";
import { FormInputValue } from "shared/types/FormInputValue";
import {
  isAlphanumericKeyEvent,
  isModifierKeyEvent,
  isPrintableKeyEvent,
} from "shared/utils/KeyboardEventUtils";
import reactToText from "shared/utils/ReactToText";

const BUFFER_TIMEOUT = 1000;

/**
 * Emulates default browser <select> behavior of jumping to the first option
 * that starts with the typed characters.
 */
export const useSelectJump = <
  T extends FormInputValue = string,
  IsMulti extends boolean = false,
>(
  ref: RefObject<SelectInstance<SelectOptionData, IsMulti>>,
  isSearchable: boolean,
  isMulti: boolean | undefined,
  options: OptionsOrGroups<SelectOptionData, GroupBase<SelectOptionData>>,
  value: (IsMulti extends true ? T[] : T) | undefined | null,
  onChangeValue?: (value: IsMulti extends true ? T[] : T) => void,
) => {
  const [keyBuffer, setKeyBuffer] = useState<string>();
  const [lastKeyAt, setLastKeyAt] = useState<number>(0);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isSearchable) {
        return;
      }

      const now = Date.now();
      const isBufferValid = !!keyBuffer && now - lastKeyAt < BUFFER_TIMEOUT;

      setLastKeyAt(now);

      const isAlphanumeric = isAlphanumericKeyEvent(event);
      const isPrintable = isPrintableKeyEvent(event);
      const isModifier = isModifierKeyEvent(event);
      const isWhitespace = event.key === " ";

      if (isWhitespace) {
        // allow default behavior for space bar to open menu when buffer is empty
        if (isBufferValid) {
          event.preventDefault();
        }
        // don't put whitespace into buffer
        return;
      }

      if (!isAlphanumeric) {
        // don't put non-alphanumeric characters into buffer
        if (!isPrintable && !isModifier) {
          // clear buffer when non-printable/non-modifier key is pressed,
          // e.g. arrow keys, enter, tab, escape, etc.
          setKeyBuffer(undefined);
        }
        return;
      }

      const key = event.key.toLowerCase();
      if (!isBufferValid) {
        setKeyBuffer(key);
      } else {
        setKeyBuffer(keyBuffer + key);
      }
    },
    [isSearchable, keyBuffer, lastKeyAt],
  );

  useEffect(() => {
    const isBufferValid =
      !!keyBuffer && Date.now() - lastKeyAt < BUFFER_TIMEOUT;

    if (isSearchable || isMulti || !isBufferValid) {
      return;
    }

    const selectInstance = ref.current;
    if (!selectInstance) {
      return;
    }

    const { menuIsOpen } = selectInstance.props;

    const jumpOptions = menuIsOpen
      ? selectInstance.getFocusableOptions()
      : options.filter(isSelectOptionData);

    const option = jumpOptions
      .toSorted((a, b) =>
        normalizeLabel(a.label).localeCompare(normalizeLabel(b.label)),
      )
      .findLast((op) => normalizeLabel(op.label).startsWith(keyBuffer));

    if (!option) {
      return;
    }

    if (menuIsOpen) {
      selectInstance.blockOptionHover = false;
      selectInstance.scrollToFocusedOptionOnUpdate = true;
      selectInstance.onOptionHover(option);
    } else {
      // this gets stuck in a loop if the option is already selected
      if (value !== option.value) {
        selectInstance.selectOption(option);
      }
    }
  }, [
    isMulti,
    isSearchable,
    onChangeValue,
    options,
    ref,
    value,
    keyBuffer,
    lastKeyAt,
  ]);

  return onKeyDown;
};

const normalizeLabel = (label: React.ReactNode) =>
  reactToText(label)
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]/g, "");
